import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
    fetchAKSClusters,
    fetchNamespacesForCluster,
    fetchServicesForClusterNamespace,
    fetchAllNamespacesAcrossClusters,
    fetchAllServicesAcrossClusters,
    setSelectedExplorerCluster,
    setSelectedExplorerNamespace
} from '../store/slices/monitoringSlice';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';

const KubernetesExplorerPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const {
        aksClusters,
        explorerNamespaces,
        explorerServices,
        explorerAllNamespaces,
        explorerAllServices,
        explorerLoading,
        explorerError,
        selectedExplorerCluster,
        selectedExplorerNamespace
    } = useSelector((state: RootState) => state.monitoring);

    // Load clusters on mount
    useEffect(() => {
        dispatch(fetchAKSClusters());
        dispatch(fetchAllNamespacesAcrossClusters());
        dispatch(fetchAllServicesAcrossClusters());
    }, [dispatch]);

    // Load namespaces when cluster changes
    useEffect(() => {
        if (selectedExplorerCluster) {
            dispatch(fetchNamespacesForCluster(selectedExplorerCluster));
        }
    }, [dispatch, selectedExplorerCluster]);

    // Load services when cluster or namespace changes
    useEffect(() => {
        if (selectedExplorerCluster && selectedExplorerNamespace) {
            dispatch(fetchServicesForClusterNamespace({
                clusterName: selectedExplorerCluster,
                namespace: selectedExplorerNamespace
            }));
        }
    }, [dispatch, selectedExplorerCluster, selectedExplorerNamespace]);

    return (
        <div className="container mx-auto px-4 py-6">
            <Breadcrumbs />
            <h1 className="text-2xl font-bold mb-4">Kubernetes Explorer</h1>
            {/* Cluster Selector */}
            <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Cluster</label>
                <select
                    className="border rounded px-2 py-1"
                    value={selectedExplorerCluster || ''}
                    onChange={e => dispatch(setSelectedExplorerCluster(e.target.value || null))}
                >
                    <option value="">-- Select Cluster --</option>
                    {aksClusters.map(cluster => (
                        <option key={cluster.name} value={cluster.name}>{cluster.name}</option>
                    ))}
                </select>
            </div>
            {/* Namespace Selector */}
            {selectedExplorerCluster && (
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Namespace</label>
                    <select
                        className="border rounded px-2 py-1"
                        value={selectedExplorerNamespace || ''}
                        onChange={e => dispatch(setSelectedExplorerNamespace(e.target.value || null))}
                    >
                        <option value="">-- Select Namespace --</option>
                        {explorerNamespaces.map(ns => (
                            <option key={ns.name} value={ns.name}>{ns.name}</option>
                        ))}
                    </select>
                </div>
            )}
            {/* Services Table */}
            {selectedExplorerCluster && selectedExplorerNamespace && (
                <div className="mb-8">
                    <h2 className="text-lg font-semibold mb-2">Services in Namespace</h2>
                    {explorerLoading ? (
                        <div>Loading services...</div>
                    ) : (
                        <table className="min-w-full border">
                            <thead>
                                <tr>
                                    <th className="border px-2 py-1">Name</th>
                                    <th className="border px-2 py-1">Type</th>
                                    <th className="border px-2 py-1">ClusterIP</th>
                                    <th className="border px-2 py-1">Ports</th>
                                    <th className="border px-2 py-1">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {explorerServices.map(svc => (
                                    <tr key={svc.name} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => navigate(`/kubernetes/services/${selectedExplorerCluster}/${selectedExplorerNamespace}/${svc.name}`)}>
                                        <td className="border px-2 py-1">{svc.name}</td>
                                        <td className="border px-2 py-1">{svc.type}</td>
                                        <td className="border px-2 py-1">{svc.clusterIP}</td>
                                        <td className="border px-2 py-1">{svc.ports?.map((p: any) => p.port).join(', ')}</td>
                                        <td className="border px-2 py-1">{svc.creationTimestamp}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            {/* All Namespaces Table */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold mb-2">All Namespaces (Cross-Cluster)</h2>
                {explorerLoading ? (
                    <div>Loading namespaces...</div>
                ) : (
                    <table className="min-w-full border">
                        <thead>
                            <tr>
                                <th className="border px-2 py-1">Cluster</th>
                                <th className="border px-2 py-1">Namespace</th>
                                <th className="border px-2 py-1">Status</th>
                                <th className="border px-2 py-1">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {explorerAllNamespaces
                                .filter(ns => !selectedExplorerCluster || ns.cluster === selectedExplorerCluster)
                                .map(ns => (
                                    <tr key={ns.cluster + '-' + ns.name} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => navigate(`/kubernetes/namespaces/${ns.cluster}/${ns.name}`)}>
                                        <td className="border px-2 py-1">{ns.cluster}</td>
                                        <td className="border px-2 py-1">{ns.name}</td>
                                        <td className="border px-2 py-1">{ns.status}</td>
                                        <td className="border px-2 py-1">{ns.creationTimestamp}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                )}
            </div>
            {/* All Services Table */}
            <div>
                <h2 className="text-lg font-semibold mb-2">All Services (Cross-Cluster & Namespace)</h2>
                {explorerLoading ? (
                    <div>Loading services...</div>
                ) : (
                    <table className="min-w-full border">
                        <thead>
                            <tr>
                                <th className="border px-2 py-1">Cluster</th>
                                <th className="border px-2 py-1">Namespace</th>
                                <th className="border px-2 py-1">Name</th>
                                <th className="border px-2 py-1">Type</th>
                                <th className="border px-2 py-1">ClusterIP</th>
                                <th className="border px-2 py-1">Ports</th>
                                <th className="border px-2 py-1">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {explorerAllServices
                                .filter(svc =>
                                    (!selectedExplorerCluster || svc.cluster === selectedExplorerCluster) &&
                                    (!selectedExplorerNamespace || svc.namespace === selectedExplorerNamespace)
                                )
                                .map(svc => (
                                    <tr key={svc.cluster + '-' + svc.namespace + '-' + svc.name} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => navigate(`/kubernetes/services/${svc.cluster}/${svc.namespace}/${svc.name}`)}>
                                        <td className="border px-2 py-1">{svc.cluster}</td>
                                        <td className="border px-2 py-1">{svc.namespace}</td>
                                        <td className="border px-2 py-1">{svc.name}</td>
                                        <td className="border px-2 py-1">{svc.type}</td>
                                        <td className="border px-2 py-1">{svc.clusterIP}</td>
                                        <td className="border px-2 py-1">{svc.ports?.map((p: any) => p.port).join(', ')}</td>
                                        <td className="border px-2 py-1">{svc.creationTimestamp}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                )}
            </div>
            {explorerError && <div className="text-red-600 mt-4">{explorerError}</div>}
        </div>
    );
};

export default KubernetesExplorerPage;
